import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
} from '@chakra-ui/react';
import { T } from '@transifex/react';
import { GroupBase, Select as ReactSelect } from 'chakra-react-select';
import { InputFormControl } from 'features/common/components/Form';
import { useAddSearchResource } from 'features/context-ids/hooks/useAddSearchResource';
import { CONTEXT_IDS_TEXT } from 'features/context-ids/constants';

interface AddSearchResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  sitecoreContextId: string;
  existingSearchTenantIds?: string[];
}

type SearchTenantOption = {
  value: string;
  label: string;
};

export const AddSearchResourceModal = ({
  isOpen,
  onClose,
  sitecoreContextId,
  existingSearchTenantIds = [],
}: AddSearchResourceModalProps) => {
  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    handleClose,
    isSubmitDisabled,
    isLoading,
    isLoadingTenants,
    isTenantsError,
    searchTenantOptions,
  } = useAddSearchResource({
    sitecoreContextId,
    onClose,
    excludedTenantIds: existingSearchTenantIds,
  });

  const selectedTenantOption =
    searchTenantOptions.find((option) => option.value === values.tenantId) ??
    null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <form onSubmit={handleSubmit}>
        <ModalOverlay />
        <ModalContent data-testid='add-search-resource-modal' minH='620px'>
          <ModalHeader>
            {CONTEXT_IDS_TEXT.ADD_SEARCH_RESOURCE_TITLE}
          </ModalHeader>
          <ModalCloseButton isDisabled={isLoading} />
          <ModalBody>
            <Stack gap={5}>
              <InputFormControl
                label={CONTEXT_IDS_TEXT.ADD_SEARCH_RESOURCE_DOMAIN_ID_LABEL}
                labelTooltip={
                  CONTEXT_IDS_TEXT.ADD_SEARCH_RESOURCE_DOMAIN_ID_TOOLTIP
                }
                name='domainId'
                value={values.domainId}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={Boolean(touched.domainId && errors.domainId)}
                error={touched.domainId ? errors.domainId : undefined}
                isRequired
                maxW='xl'
                placeholder='Enter domain ID'
                autoComplete='off'
                maxLength={255}
              />
              <InputFormControl
                label={CONTEXT_IDS_TEXT.ADD_SEARCH_RESOURCE_REGION_LABEL}
                labelTooltip={
                  CONTEXT_IDS_TEXT.ADD_SEARCH_RESOURCE_REGION_TOOLTIP
                }
                name='region'
                value={values.region}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={Boolean(touched.region && errors.region)}
                error={touched.region ? errors.region : undefined}
                isRequired
                maxW='xl'
                placeholder='Enter region'
                autoComplete='off'
                maxLength={100}
              />
              <InputFormControl
                label={CONTEXT_IDS_TEXT.ADD_SEARCH_RESOURCE_API_KEY_LABEL}
                labelTooltip={
                  CONTEXT_IDS_TEXT.ADD_SEARCH_RESOURCE_API_KEY_TOOLTIP
                }
                name='apiKey'
                value={values.apiKey}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={Boolean(touched.apiKey && errors.apiKey)}
                error={touched.apiKey ? errors.apiKey : undefined}
                isRequired
                maxW='xl'
                placeholder='Enter API key'
                type='password'
                autoComplete='new-password'
                maxLength={512}
                showPasswordToggle
                resetVisibilityKey={isOpen}
                helperText={
                  <>
                    {CONTEXT_IDS_TEXT.ADD_SEARCH_RESOURCE_API_KEY_DOCS_PREFIX}{' '}
                    <Link
                      href='https://doc.sitecore.com/search/en/developers/search-developer-guide/api-authentication-and-authorization.html#api-key'
                      isExternal
                      color='blue.500'
                    >
                      {
                        CONTEXT_IDS_TEXT.ADD_SEARCH_RESOURCE_API_KEY_DOCS_LINK_TEXT
                      }
                    </Link>
                  </>
                }
              />
              <FormControl
                isRequired
                isInvalid={Boolean(touched.tenantId && errors.tenantId)}
                maxW='full'
              >
                <FormLabel htmlFor='tenantId'>
                  <T _str={CONTEXT_IDS_TEXT.ADD_SEARCH_RESOURCE_TENANT_LABEL} />
                </FormLabel>
                <Skeleton isLoaded={!isLoadingTenants}>
                  {isTenantsError ? (
                    <Alert status='error' data-testid='tenant-load-error'>
                      <AlertIcon />
                      Failed to load Search environments. Please try again.
                    </Alert>
                  ) : (
                    <ReactSelect<
                      SearchTenantOption,
                      false,
                      GroupBase<SearchTenantOption>
                    >
                      inputId='tenantId'
                      value={selectedTenantOption}
                      options={searchTenantOptions}
                      onChange={(selectedOption) =>
                        setFieldValue('tenantId', selectedOption?.value ?? '')
                      }
                      onBlur={() => setFieldTouched('tenantId', true)}
                      placeholder='Select Search environment'
                      isDisabled={isLoading || searchTenantOptions.length === 0}
                      menuPlacement='auto'
                      maxMenuHeight={176}
                      chakraStyles={{
                        container: (provided) => ({
                          ...provided,
                          width: '100%',
                          maxWidth: '100%',
                        }),
                      }}
                    />
                  )}
                </Skeleton>
                {!isLoadingTenants &&
                  !isTenantsError &&
                  searchTenantOptions.length === 0 && (
                    <Alert
                      status='info'
                      mt={2}
                      data-testid='tenant-empty-state'
                    >
                      <AlertIcon />
                      No Search environments are available for your
                      organization.
                    </Alert>
                  )}
                {touched.tenantId && errors.tenantId && (
                  <FormErrorMessage>{errors.tenantId}</FormErrorMessage>
                )}
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                data-testid='add-search-resource-cancel-btn'
                type='button'
                variant='ghost'
                onClick={handleClose}
                isDisabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                data-testid='add-search-resource-submit-btn'
                type='submit'
                variant='solid'
                isLoading={isLoading}
                isDisabled={isSubmitDisabled}
              >
                Add
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
};
